export enum PerformanceContributionGroupingOption {
  /**
   * Default for Portfolios, QIS Baskets, Benchmarks, and Peer Groups, see `PortfolioPerformanceContributionGroupingOption`
   *
   * In order to be backwards compatible with the API, this string must be used for both Strategy and Constituent Asset Class
   *
   * In Portfolio Performance Contribution, the Asset Class refers to the Strategy Asset Class
   * In Strategy Performance Contribution, the Asset Class refers to the Constituent Asset Class
   */
  STRATEGY_OR_CONSTITUENT_ASSET_CLASS = 'Asset Class',

  /**
   * In order to be backwards compatible with the API, this string must be used for both Strategy and Constituent Currency
   *
   * In Portfolio Performance Contribution, the Currency refers to the Strategy Currency
   * In Strategy Performance Contribution, the Currency refers to the Constituent Currency
   */
  STRATEGY_OR_CONSTITUENT_CURRENCY = 'Currency',
  STRATEGY_FACTOR = 'Factor',
  STRATEGY_PROVIDER = 'Provider',
  /**
   * In order to be backwards compatible with the API, this string must be used for both Strategy and Constituent Region
   *
   * In Portfolio Performance Contribution, the Region refers to the Strategy Region
   * In Strategy Performance Contribution, the Region refers to the Constituent Region (may not yet be supported)
   */
  STRATEGY_OR_CONSTITUENT_REGION = 'Region',
  STRATEGY_RETURN_TYPE = 'Return Type',
  /**
   * All strategies in the portfolio, regardless of where they are nested
   * Default for Portfolio Performance Contribution
   */
  STRATEGIES_ALL = 'Strategy',
  STRATEGY_STYLE = 'Style',
  STRATEGY_TYPE = 'Type',

  // plus custom properties on strategies that users have

  /**
   * Every first level item under the root portfolio
   */
  PORTFOLIO_LEVEL_ONE = 'Portfolio',

  CONSTITUENT_COUNTRY = 'Country',
  CONSTITUENT_INSTRUMENT = 'Instrument',
  CONSTITUENT_LONG_SHORT = 'Long/Short',

  CONSTITUENT_SECTOR = 'Sector',
  CONSTITUENT_PRODUCT_TYPE = 'Product Type',

  CONSTITUENT_CURRENCY_OF_RISK = 'Currency of Risk',
  CONSTITUENT_UNDERLYING = 'Underlying',

  BARRA_FACTOR = 'Barra Factor',
  FACTOR_GROUP = 'Factor Group',
}

/**
 * Used for Portfolios, QIS Baskets, Benchmarks, and Peer Groups
 *
 * Default is PerformanceContributionGroupingOption.STRATEGIES_ALL
 */
export enum PortfolioPerformanceContributionGroupingOption {
  STRATEGY_ASSET_CLASS = PerformanceContributionGroupingOption.STRATEGY_OR_CONSTITUENT_ASSET_CLASS,
  STRATEGY_CURRENCY = PerformanceContributionGroupingOption.STRATEGY_OR_CONSTITUENT_CURRENCY,
  STRATEGY_FACTOR = PerformanceContributionGroupingOption.STRATEGY_FACTOR,
  STRATEGY_REGION = PerformanceContributionGroupingOption.STRATEGY_OR_CONSTITUENT_REGION,

  /**
   * TODO: WAA-14761 confirm why Christophe's Confluence is missing this one, even though it's implemented in the API
   */
  STRATEGY_TYPE = PerformanceContributionGroupingOption.STRATEGY_TYPE,

  /**
   * All strategies in the portfolio, regardless of where they are nested
   *
   * TODO: WAA-14761 confirm why this is marked red on the Confluence
   */
  STRATEGIES_ALL = PerformanceContributionGroupingOption.STRATEGIES_ALL,
  PORTFOLIO_LEVEL_ONE = PerformanceContributionGroupingOption.PORTFOLIO_LEVEL_ONE,

  // not yet implemented in the API
  // STRATEGY_PROVIDER = PerformanceContributionGroupingOption.STRATEGY_PROVIDER,
  // STRATEGY_RETURN_TYPE = PerformanceContributionGroupingOption.STRATEGY_RETURN_TYPE,
  // STRATEGY_STYLE = PerformanceContributionGroupingOption.STRATEGY_STYLE,
}

/**
 * Used for Equity Baskets and Thematics
 *
 * Default is PerformanceContributionGroupingOption.STRATEGIES_ALL
 * In the future on Equity Baskets, we want to rename 'Strategy' to 'Instrument'
 * However, at the moment the API does not support this, so we are using 'Strategy' as functionally they are the same
 */
export enum EquityBasketAndThematicPerformanceContributionGroupingOption {
  CONSTITUENT_SECTOR = PerformanceContributionGroupingOption.CONSTITUENT_SECTOR,
  CONSTITUENT_COUNTRY = PerformanceContributionGroupingOption.CONSTITUENT_COUNTRY,
  CONSTITUENT_LONG_SHORT = PerformanceContributionGroupingOption.CONSTITUENT_LONG_SHORT,
  PORTFOLIO_LEVEL_ONE = PerformanceContributionGroupingOption.PORTFOLIO_LEVEL_ONE,

  // not yet implemented in the API
  // CONSTITUENT_CURRENCY = PerformanceContributionGroupingOption.CONSTITUENT_CURRENCY,
  // CONSTITUENT_REGION = PerformanceContributionGroupingOption.CONSTITUENT_REGION,
  // CONSTITUENT_PRODUCT_TYPE = PerformanceContributionGroupingOption.CONSTITUENT_PRODUCT_TYPE,

  // TODO: WAA-14761 remove these options (keeping for backwards compatibility for now)
  STRATEGY_ASSET_CLASS = PerformanceContributionGroupingOption.STRATEGY_OR_CONSTITUENT_ASSET_CLASS,
  STRATEGY_CURRENCY = PerformanceContributionGroupingOption.STRATEGY_OR_CONSTITUENT_CURRENCY,
  STRATEGY_FACTOR = PerformanceContributionGroupingOption.STRATEGY_FACTOR,
  STRATEGY_REGION = PerformanceContributionGroupingOption.STRATEGY_OR_CONSTITUENT_REGION,

  /**
   * TODO: WAA-14761 understand why Christophe's Confluence is missing this one, even though it's implemented in the API
   */
  STRATEGY_TYPE = PerformanceContributionGroupingOption.STRATEGY_TYPE,

  /**
   * All strategies in the portfolio, regardless of where they are nested
   *
   * TODO: WAA-14761 understand why this is marked red on the Confluence
   */
  STRATEGIES_ALL = PerformanceContributionGroupingOption.STRATEGIES_ALL,

  BARRA_FACTOR = PerformanceContributionGroupingOption.BARRA_FACTOR,

  // not yet implemented in the API
  // FACTOR_GROUP = PerformanceContributionGroupingOption.FACTOR_GROUP,
  // STRATEGY_PROVIDER = PerformanceContributionGroupingOption.STRATEGY_PROVIDER,
  // STRATEGY_RETURN_TYPE = PerformanceContributionGroupingOption.STRATEGY_RETURN_TYPE,
  // STRATEGY_STYLE = PerformanceContributionGroupingOption.STRATEGY_STYLE,
}

export enum StrategyPerformanceContributionGroupingOption {
  CONSTITUENT_ASSET_CLASS = PerformanceContributionGroupingOption.STRATEGY_OR_CONSTITUENT_ASSET_CLASS,
  CONSTITUENT_CURRENCY = PerformanceContributionGroupingOption.STRATEGY_OR_CONSTITUENT_CURRENCY,
  CONSTITUENT_CURRENCY_OF_RISK = PerformanceContributionGroupingOption.CONSTITUENT_CURRENCY_OF_RISK,
  CONSTITUENT_COUNTRY = PerformanceContributionGroupingOption.CONSTITUENT_COUNTRY,
  CONSTITUENT_INSTRUMENT = PerformanceContributionGroupingOption.CONSTITUENT_INSTRUMENT,
  CONSTITUENT_LONG_SHORT = PerformanceContributionGroupingOption.CONSTITUENT_LONG_SHORT,
  CONSTITUENT_PRODUCT_TYPE = PerformanceContributionGroupingOption.CONSTITUENT_PRODUCT_TYPE,
  CONSTITUENT_SECTOR = PerformanceContributionGroupingOption.CONSTITUENT_SECTOR,
  CONSTITUENT_UNDERLYING = PerformanceContributionGroupingOption.CONSTITUENT_UNDERLYING,
}
